import { forwardRef, useId } from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cn } from '../cn';

interface FormFieldProps {
    label: string;
    required?: boolean;
    error?: string;
    hint?: string;
    children: (id: string) => React.ReactNode;
    className?: string;
}

export function FormField({ label, required, error, hint, children, className }: FormFieldProps) {
    const id = useId();
    return (
        <div className={cn('mb-3.5', className)}>
            <LabelPrimitive.Root
                htmlFor={id}
                className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5"
            >
                {label}
                {required && <span className="text-accent2-text ml-0.5">*</span>}
            </LabelPrimitive.Root>
            {children(id)}
            {hint && !error && <p className="mt-1 text-xs text-text-subtle">{hint}</p>}
            {error && <p className="mt-1 text-xs text-error">{error}</p>}
        </div>
    );
}

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
    ({ className, ...props }, ref) => (
        <input
            ref={ref}
            className={cn(
                'w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text',
                'placeholder:text-text-subtle',
                'focus:border-accent focus:outline-none',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors duration-[var(--duration-base)]',
                className,
            )}
            {...props}
        />
    ),
);
Input.displayName = 'Input';
